/**
 * WorkflowStages Component
 * Displays workflow stages in vertical hierarchical order
 * with visual indicators for completed and pending stages
 */

import React, { useEffect, useState } from "react";
import { workflowService } from "../../services/api/workflow.service";
import { rolesService } from "../../services/api/roles.service";
import type { WorkflowStage } from "../../types/workflow.types";
import type { Role } from "../../types/role.types";
import "./WorkflowStages.css";

interface WorkflowStagesProps {
  workflowId: number;
  currentStageOrder?: number; // The current stage in the workflow (all stages before this are completed)
}

export const WorkflowStages: React.FC<WorkflowStagesProps> = ({
  workflowId,
  currentStageOrder = 0,
}) => {
  const [stages, setStages] = useState<WorkflowStage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchWorkflowStages();
  }, [workflowId]);

  const fetchWorkflowStages = async () => {
    try {
      setLoading(true);
      setError("");

      // Fetch workflow and roles in parallel
      const [workflow, roles] = await Promise.all([
        workflowService.getWorkflowById(workflowId),
        rolesService.getRoles(),
      ]);

      if (workflow.stages) {
        // Map role names to stages
        const stagesWithRoleNames = workflow.stages.map((stage) => {
          const role = roles.find((r: Role) => r.id === stage.roleId);
          return {
            ...stage,
            roleName: role?.name || stage.roleName || "Unknown Role",
          };
        });

        // Sort stages by stageOrder
        const sortedStages = [...stagesWithRoleNames].sort(
          (a, b) => a.stageOrder - b.stageOrder,
        );
        setStages(sortedStages);
      } else {
        setStages([]);
      }
    } catch (err: any) {
      console.error("Error fetching workflow stages:", err);
      setError(err?.message || "Failed to load workflow stages");
    } finally {
      setLoading(false);
    }
  };

  const getStageStatus = (
    stageOrder: number,
  ): "completed" | "current" | "pending" => {
    if (stageOrder < currentStageOrder) {
      return "completed";
    } else if (stageOrder === currentStageOrder) {
      return "current";
    }
    return "pending";
  };

  if (loading) {
    return (
      <div className="workflow-stages">
        <div className="workflow-stages-loading">
          Loading workflow stages...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="workflow-stages">
        <div className="workflow-stages-error">⚠️ {error}</div>
      </div>
    );
  }

  if (stages.length === 0) {
    return (
      <div className="workflow-stages">
        <div className="workflow-stages-empty">No workflow stages defined</div>
      </div>
    );
  }

  return (
    <div className="workflow-stages">
      <h3 className="workflow-stages-title">Workflow Progress</h3>
      <div className="workflow-stages-timeline">
        {stages.map((stage, index) => {
          const status = getStageStatus(stage.stageOrder);
          const isLast = index === stages.length - 1;

          return (
            <div
              key={stage.id}
              className={`workflow-stage workflow-stage--${status}`}
            >
              <div className="workflow-stage-marker">
                <div className="workflow-stage-icon">
                  {status === "completed" && "✓"}
                  {status === "current" && "●"}
                  {status === "pending" && "○"}
                </div>
                {!isLast && <div className="workflow-stage-line"></div>}
              </div>
              <div className="workflow-stage-content">
                <div className="workflow-stage-name">
                  <span className="workflow-stage-number">{index + 1}.</span>{" "}
                  {stage.name}
                </div>
                <div className="workflow-stage-role">
                  <span className="workflow-stage-role-label">Approver:</span>{" "}
                  {stage.roleName}
                </div>
                {stage.description && (
                  <div className="workflow-stage-description">
                    {stage.description}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default WorkflowStages;
